<template>
  <div id="program-extra-pages" class="program-extra-pages">
    <div v-if="editMode" class="program-extra-pages__controls no-print">
      <div class="program-extra-pages__controls-title">Extra pages</div>
      <g-button label="Add page" icon="add" class="mb-2" @onclick="openPicker" />
      <div v-for="(entry, index) in resolvedEntries" :key="`extra-ctrl-${index}`" class="program-extra-pages__control-row">
        <div class="program-extra-pages__control-label">
          <strong>{{ controlLabel(entry) }}</strong>
          <span class="caption ml-2">{{ formatType(entry.type) }}</span>
          <span v-if="detachEnabled && entry.mode === 'detached'" class="caption orange--text ml-2">detached</span>
        </div>
        <div class="program-extra-pages__control-actions">
          <v-btn icon small @click="moveEntry(index, -1)" :disabled="index === 0">
            <v-icon small>arrow_upward</v-icon>
          </v-btn>
          <v-btn icon small @click="moveEntry(index, 1)" :disabled="index === resolvedEntries.length - 1">
            <v-icon small>arrow_downward</v-icon>
          </v-btn>
          <v-btn
            v-if="detachEnabled && entry.mode === 'live'"
            icon
            small
            title="Detach"
            @click="detachEntry(index)"
          >
            <v-icon small>call_split</v-icon>
          </v-btn>
          <v-btn
            v-else-if="detachEnabled && entry.mode === 'detached'"
            icon
            small
            title="Edit detached"
            @click="openDetachedEditor(index)"
          >
            <v-icon small>edit</v-icon>
          </v-btn>
          <v-btn icon small color="error" @click="removeEntry(index)">
            <v-icon small>delete</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <div
      v-for="(sheet, sheetIndex) in renderedSheets"
      :key="`extra-sheet-${sheet.entryKey}-${sheet.pageIndex}-${sheetIndex}`"
      class="program-page program-extra-page"
      :class="{ 'program-extra-page--map': isMapPageType(sheet.type) }"
    >
      <QuoteProgramWrapperHeader :quote="quote" :show-details="false" class="program-page-header" />
      <div class="program-extra-page__inner">
        <div
          v-if="!isMapPageType(sheet.type) && sheet.showTitle"
          class="program-extra-page__title-block"
        >
          <div v-if="sheet.eyebrow" class="program-extra-page__eyebrow">{{ sheet.eyebrow }}</div>
          <h1 class="program-extra-page__title">{{ sheet.mainTitle }}</h1>
          <div class="program-extra-page__title-rule"></div>
        </div>
        <div class="program-extra-page__body program-extra-page__html" v-html="sheet.html"></div>
      </div>
    </div>

    <div ref="measureHost" class="program-extra-pages__measure" aria-hidden="true"></div>

    <QuoteProgramPageDialog v-model="pageDialog" @select="onPageSelect" />
    <QuoteProgramDetachedPageDialog
      v-if="detachEnabled && detachedDialog"
      v-model="detachedDialog"
      @save="onDetachedSave"
    />
  </div>
</template>

<script>
import QuoteProgramWrapperHeader from "../QuoteProgramWrapperHeader";
import QuoteProgramPageDialog from "../QuoteProgramPageDialog";
import QuoteProgramDetachedPageDialog from "../QuoteProgramDetachedPageDialog";
import { pageTypeLabel, pageInternalLabel, normalizePageContent, clonePageContent, isMapPageType } from "@/modules/program_pages";
import { packProgramPageHtml, watchImagesForRepack } from "@/modules/program_pages/programPagePacking";

function splitExtraPageTitle(title) {
  const lines = String(title || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (!lines.length) {
    return { eyebrow: "", mainTitle: "Untitled page" };
  }
  if (lines.length === 1) {
    return { eyebrow: "", mainTitle: lines[0] };
  }
  return {
    eyebrow: lines[0],
    mainTitle: lines.slice(1).join("\n"),
  };
}
export default {
  components: {
    QuoteProgramWrapperHeader,
    QuoteProgramPageDialog,
    QuoteProgramDetachedPageDialog,
  },
  props: {
    quote: {
      type: Object,
      required: true,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    images: {
      type: Array,
      default: () => [],
    },
    programPages: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      // Hidden for now — set true to restore detach / edit-detached controls.
      detachEnabled: false,
      pageDialog: null,
      detachedDialog: null,
      renderedSheets: [],
      packQueued: false,
      packTimer: null,
      imagePackPasses: 0,
      unwatchImages: null,
    };
  },
  computed: {
    extraPages() {
      return Array.isArray(this.quote?.program?.extraPages) ? this.quote.program.extraPages : [];
    },
    libraryById() {
      const map = {};
      (this.programPages || []).forEach((page) => {
        if (page?.id) map[page.id] = page;
      });
      return map;
    },
    resolvedEntries() {
      return this.extraPages
        .map((entry) => {
          if (!entry) return null;
          if (entry.mode === "detached") {
            const type = entry.type || "excursions_visites";
            return {
              mode: "detached",
              sourcePageId: entry.sourcePageId || null,
              title: entry.title || "",
              internalTitle: entry.internalTitle || entry.title || "",
              type,
              content: normalizePageContent(type, entry.content),
            };
          }
          const libraryPage = this.libraryById[entry.pageId];
          if (!libraryPage) {
            return {
              mode: "live",
              pageId: entry.pageId,
              title: "Missing page",
              internalTitle: "Missing page",
              type: "excursions_visites",
              content: { html: "<p>This program page is no longer available.</p>" },
            };
          }
          const type = libraryPage.type || "excursions_visites";
          return {
            mode: "live",
            pageId: libraryPage.id,
            title: libraryPage.title || "",
            internalTitle: pageInternalLabel(libraryPage),
            type,
            content: normalizePageContent(type, libraryPage.content),
          };
        })
        .filter(Boolean);
    },
    entriesSignature() {
      // Fingerprint only — never stringify full HTML (base64 images blow memory).
      return this.resolvedEntries
        .map((entry) => {
          const html = (entry.content && entry.content.html) || "";
          return [
            entry.mode,
            entry.pageId || "",
            entry.sourcePageId || "",
            entry.title || "",
            entry.type || "",
            html.length,
            html.slice(0, 24),
            html.slice(-24),
          ].join("|");
        })
        .join("||");
    },
  },
  watch: {
    entriesSignature: {
      handler() {
        this.imagePackPasses = 0;
        this.queuePack();
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    if (this.packTimer) clearTimeout(this.packTimer);
    if (this.unwatchImages) this.unwatchImages();
  },
  methods: {
    formatType(type) {
      return pageTypeLabel(type);
    },
    controlLabel(entry) {
      return entry.internalTitle || entry.title || "Untitled";
    },
    isMapPageType,
    queuePack({ fromImages = false } = {}) {
      if (fromImages) {
        // Safety valve: never allow image-driven re-packs to run unbounded.
        if (this.imagePackPasses >= 5) return;
        this.imagePackPasses += 1;
      }
      if (this.packQueued) return;
      this.packQueued = true;
      if (this.packTimer) clearTimeout(this.packTimer);
      this.packTimer = setTimeout(() => {
        this.packTimer = null;
        this.packQueued = false;
        this.packSheets();
      }, fromImages ? 50 : 0);
    },
    packSheets() {
      if (this.unwatchImages) {
        this.unwatchImages();
        this.unwatchImages = null;
      }

      const host = this.$refs.measureHost;
      const sheets = [];
      const cleanups = [];
      const watchImages = this.imagePackPasses < 5;

      this.resolvedEntries.forEach((entry, entryIndex) => {
        const html = normalizePageContent(entry.type, entry.content).html || "";
        const pages = packProgramPageHtml(html, host, {
          hasTitle: !isMapPageType(entry.type),
          htmlClassName: "program-extra-page__html",
        });
        const pageHtmls = pages.length ? pages : html.trim() ? [html] : [""];
        pageHtmls.forEach((pageHtml, pageIndex) => {
          const titleParts = splitExtraPageTitle(entry.title);
          sheets.push({
            entryKey: `${entry.mode}-${entry.pageId || entry.sourcePageId || entryIndex}`,
            pageIndex,
            type: entry.type,
            title: entry.title,
            eyebrow: titleParts.eyebrow,
            mainTitle: titleParts.mainTitle,
            showTitle: pageIndex === 0,
            html: pageHtml,
          });
        });
        if (watchImages) {
          cleanups.push(watchImagesForRepack(html, () => this.queuePack({ fromImages: true })));
        }
      });

      this.renderedSheets = sheets;
      this.unwatchImages = () => cleanups.forEach((fn) => fn && fn());
    },
    emitExtraPages(extraPages) {
      const updatedQuote = {
        ...this.quote,
        program: {
          ...(this.quote.program || {}),
          extraPages,
        },
      };
      this.$emit("update", { quote: updatedQuote });
    },
    openPicker() {
      this.pageDialog = { pageId: null };
    },
    onPageSelect(page) {
      if (!page?.id) return;
      const extraPages = [...this.extraPages, { mode: "live", pageId: page.id }];
      const payload = {
        quote: {
          ...this.quote,
          program: {
            ...(this.quote.program || {}),
            extraPages,
          },
        },
        programPage: page,
      };
      this.$emit("update", payload);
    },
    moveEntry(index, delta) {
      const next = index + delta;
      if (next < 0 || next >= this.extraPages.length) return;
      const extraPages = this.extraPages.slice();
      const tmp = extraPages[index];
      extraPages[index] = extraPages[next];
      extraPages[next] = tmp;
      this.emitExtraPages(extraPages);
    },
    removeEntry(index) {
      const extraPages = this.extraPages.slice();
      extraPages.splice(index, 1);
      this.emitExtraPages(extraPages);
    },
    detachEntry(index) {
      const entry = this.extraPages[index];
      if (!entry || entry.mode !== "live") return;
      const libraryPage = this.libraryById[entry.pageId];
      if (!libraryPage) {
        this.$root.$error("Cannot detach: library page not loaded");
        return;
      }
      const type = libraryPage.type || "excursions_visites";
      const extraPages = this.extraPages.slice();
      extraPages[index] = {
        mode: "detached",
        sourcePageId: libraryPage.id,
        title: libraryPage.title || "",
        type,
        content: clonePageContent(normalizePageContent(type, libraryPage.content)),
      };
      this.emitExtraPages(extraPages);
    },
    openDetachedEditor(index) {
      const entry = this.extraPages[index];
      if (!entry || entry.mode !== "detached") return;
      this.detachedDialog = { index, page: entry };
    },
    onDetachedSave({ index, page }) {
      const extraPages = this.extraPages.slice();
      extraPages[index] = page;
      this.emitExtraPages(extraPages);
    },
  },
};
</script>

<style lang="scss">
.program-extra-pages {
  width: 780px;
  max-width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 28px;
  scroll-margin-top: 48px;
}

.program-extra-pages__controls {
  width: 100%;
  box-sizing: border-box;
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 12px;
  margin: 0;
}

.program-extra-pages__controls-title {
  font-weight: 600;
  margin-bottom: 8px;
}

.program-extra-pages__control-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 0;
  border-top: 1px solid #e0e0e0;
}

.program-extra-pages__control-actions {
  display: flex;
  align-items: center;
}

.program-extra-pages__measure {
  position: absolute;
  left: -10000px;
  top: 0;
  visibility: hidden;
  pointer-events: none;
}

.program-extra-page.program-page {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 980px;
  padding: 40px 44px 36px;
  margin: 0;
  background: #fff;
  border: 1px solid #cfcfcf;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  box-sizing: border-box;
  page-break-after: always;
  break-after: page;
}

.program-extra-page .program-page-header {
  margin-bottom: 28px;
  flex: 0 0 auto;
}

.program-extra-page__inner {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.program-extra-page__title-block {
  text-align: center;
  margin: 4px 0 24px;
}

.program-extra-page__eyebrow {
  font-family: "Calibri", sans-serif;
  font-size: 11pt;
  font-weight: 400;
  color: #c5a069;
  line-height: 1.4;
  margin: 0 0 8px;
}

.program-extra-page__title {
  font-family: "Calibri", sans-serif;
  font-size: 16pt;
  font-weight: 700;
  margin: 0 auto;
  max-width: 92%;
  color: #c5a069;
  line-height: 1.35;
  white-space: pre-line;
}

.program-extra-page__title-rule {
  width: 86%;
  max-width: 640px;
  height: 1px;
  margin: 16px auto 0;
  background: #111;
}

.program-extra-page__html {
  font-size: 13px;
  line-height: 1.35;
  color: #2a2a2a;

  h1,
  h2,
  h3 {
    margin: 0 0 10px;
    font-weight: 700;
  }

  p {
    margin: 0 0 10px;
  }

  ul,
  ol {
    margin: 0 0 12px;
    padding-left: 20px;
  }

  img {
    display: inline-block;
    vertical-align: top;
    max-width: 100%;
    height: auto;
    margin: 4px 6px 8px 0;
  }

  .rte-columns {
    display: flex;
    gap: 16px;
    width: 100%;
    margin: 12px 0;
    align-items: flex-start;
    box-sizing: border-box;
  }

  .rte-columns--icon > .rte-column:first-child {
    flex: 0 0 72px;
    max-width: 72px;
    min-width: 72px;
  }

  .rte-columns--icon > .rte-column:last-child {
    flex: 1 1 auto;
  }

  .rte-columns--icon > .rte-column:first-child img {
    width: 100%;
    max-width: 64px;
    height: auto;
    margin: 0;
  }

  .rte-column {
    flex: 1 1 0;
    min-width: 0;
    box-sizing: border-box;
  }

  hr {
    display: none;
  }
}

.program-extra-page--map .program-extra-page__html img {
  width: 100%;
  max-height: 780px;
  object-fit: contain;
}
</style>
